/* eslint-disable import/no-extraneous-dependencies */
import { useTranslation } from 'react-i18next';
import { TbArrowRight } from 'react-icons/tb';
import { Link } from 'react-router';

import { APPS } from '@cccom/config/app-config';
import { App } from '@cccom/config/app-type';
import { useEnvironment } from '@cccom/config/env';
import { Badge } from '@cccom/ui/src/components/shadcn/badge';
import {
  Card,
  CardContent,
  CardHeader,
} from '@cccom/ui/src/components/shadcn/card';
import { cn } from '@cccom/ui/src/utils';

function BgCircle({
  multiplier,
  color,
}: {
  multiplier: number;
  color: string;
}) {
  const circleSize = '430px';
  const size = `calc(${circleSize} - ${multiplier}px)`;

  return (
    <div
      className="absolute -bottom-[120%] -right-[120%] rounded-full opacity-30"
      style={{
        height: size,
        width: size,
        background: color,
      }}
    />
  );
}

export type AppCardProps = {
  app: App;
};

export function AppCard({ app }: AppCardProps) {
  const [t] = useTranslation();
  const env = useEnvironment();

  const { name, description, status, logo, theme } = app;
  const color = theme['colors'].brand;
  const brandColor = color['300'];
  const href = APPS.getUrl(env, app);
  const isFuture = status === 'FUTURE';

  const cardElement = (
    <>
      {isFuture && (
        <Badge
          variant="default"
          className="absolute right-2 top-2 z-10 h-4 bg-teal-500 px-2.5 py-0.5 text-xs font-medium text-white"
        >
          {t('home.landing.coming_soon')}
        </Badge>
      )}
      <Card
        className={cn(
          'group relative min-h-[11.5rem] w-64 overflow-hidden p-7 transition-all duration-300',
          'border-gray-100 hover:shadow-lg active:shadow-inner',
          isFuture && 'opacity-80 grayscale-[65%]'
        )}
        data-cy={name}
      >
        {/* On hover animation elements */}
        <div
          className="absolute -left-72 -right-6 top-0 h-full w-full -skew-x-[20deg] opacity-80 transition-all duration-500 group-hover:-left-8 group-hover:w-[150%]"
          style={{ background: color['50'] }}
        />

        <div className="absolute left-64 top-0 h-full w-full transition-all duration-500 group-hover:left-0">
          {Array.from({ length: 2 }).map((_, i) => {
            const index = i + 1;
            const multiplier = index * 30;

            return (
              <BgCircle
                key={index}
                multiplier={multiplier}
                color={brandColor}
              />
            );
          })}
        </div>

        <CardHeader className="relative flex flex-row items-center gap-4 p-0">
          <img
            src={logo}
            alt="logo"
            className="h-10 transition-all duration-300 group-hover:h-[30px]"
          />

          <span className="pb-2 text-base">{name}</span>
        </CardHeader>

        {description && (
          <CardContent className="relative mt-4 p-0 text-xs leading-[18px] text-gray-500">
            <p>{description}</p>
          </CardContent>
        )}

        <TbArrowRight
          className="absolute bottom-4 right-4 translate-y-3 opacity-0 transition-all duration-300 group-hover:translate-y-0 group-hover:opacity-100"
          size={18}
        />
      </Card>
    </>
  );

  if (isFuture) {
    return <div className="relative text-left">{cardElement}</div>;
  }

  return (
    <Link
      reloadDocument
      to={href}
      className="relative text-left no-underline hover:no-underline"
    >
      {cardElement}
    </Link>
  );
}
